import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { formatPurchaseOrderDateTime } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { BatchActionsMenu } from 'components/shipping/components/viewBatches/BatchActionsMenu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { ResultsCounter } from 'shared/components/pagination';

const BATCH_DISPLAY_FALLBACK = '—';

/**
 * @param {unknown} value
 * @returns {string | number}
 */
function getDisplayValue(value) {
    if (value === undefined || value === null || value === '') {
        return BATCH_DISPLAY_FALLBACK;
    }
    return value;
}

/**
 * @param {number | string | null | undefined} downloadingBatchId
 * @param {{ original?: Record<string, unknown> }} row
 * @returns {boolean}
 */
function isDownloadingBatch(downloadingBatchId, row) {
    if (downloadingBatchId === undefined || downloadingBatchId === null) {
        return false;
    }
    return downloadingBatchId === row?.original?.id;
}

const renderActionsCell = ({ row, table }) => {
    const {
        canReverseFBMSubstitution,
        downloadingBatchId,
        onApplySubstitutions,
        onDownloadFile,
        onReverseSubstitution,
    } = table.options.meta ?? {};
    return (
        <BatchActionsMenu
            batchId={row.original?.id}
            canReverseFBMSubstitution={canReverseFBMSubstitution}
            isDownloading={isDownloadingBatch(downloadingBatchId, row)}
            onApplySubstitutions={onApplySubstitutions}
            onDownloadFile={onDownloadFile}
            onReverseSubstitution={onReverseSubstitution}
            shipmentStatus={row.original?.shipmentStatus}
        />
    );
};

const MemoizedBatchRow = React.memo(({ isDownloadingFile, row }) => (
    <TableRow aria-busy={isDownloadingFile || undefined}>
        {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
        ))}
    </TableRow>
));

/**
 * TanStack table for the View Batches list.
 *
 * @param {Object} props
 * @param {Array<Record<string, unknown>>} [props.data=[]] - Batch rows from `GET /warehouse/v1/batches`
 * @param {boolean} [props.isLoading=false] - Initial loading state
 * @param {boolean} [props.isFetching=false] - Whether any fetch is in progress
 * @param {string} [props.emptyMessage='No batches found.'] - Message when table is empty
 * @param {(batchId: number | string) => void} [props.onApplySubstitutions] - Invoked when Update Substitutions is selected
 * @param {(batchId: number | string) => void} [props.onDownloadFile] - Invoked with the batch id when its print artifact is requested
 * @param {(batchId: number | string) => void} [props.onReverseSubstitution] - Invoked when Reverse Substitution For Batch is selected
 * @param {boolean} [props.canReverseFBMSubstitution=false] - Whether Reverse Substitution For Batch is available
 * @param {number | string | null} [props.downloadingBatchId] - Batch id whose file download is in flight
 * @returns {JSX.Element} Batches data table.
 */
export function BatchesTable({
    canReverseFBMSubstitution = false,
    data = [],
    downloadingBatchId,
    emptyMessage = 'No batches found.',
    isFetching = false,
    isLoading = false,
    onApplySubstitutions,
    onDownloadFile,
    onReverseSubstitution,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(
        () => [
            {
                id: 'id',
                accessorKey: 'id',
                header: 'ID',
                enableSorting: false,
                cell: ({ getValue }) => getDisplayValue(getValue()),
            },
            {
                id: 'createdAt',
                accessorKey: 'createdAt',
                header: 'Created At',
                enableSorting: false,
                cell: ({ getValue }) => formatPurchaseOrderDateTime(getValue(), BATCH_DISPLAY_FALLBACK),
            },
            {
                id: 'creator',
                accessorKey: 'creator',
                header: 'Creator',
                enableSorting: false,
                cell: ({ getValue }) => getDisplayValue(getValue()),
            },
            {
                id: 'batchStatus',
                accessorKey: 'batchStatus',
                header: 'Batch Status',
                enableSorting: false,
                cell: ({ getValue }) => getDisplayValue(getValue()),
            },
            {
                id: 'numItems',
                accessorKey: 'numItems',
                header: 'Num Items',
                enableSorting: false,
                cell: ({ getValue }) => getDisplayValue(getValue()),
            },
            {
                id: 'carrier',
                accessorKey: 'carrier',
                header: 'Carrier',
                enableSorting: false,
                cell: ({ getValue }) => getDisplayValue(getValue()),
            },
            {
                id: 'hub',
                accessorKey: 'hub',
                header: 'Hub',
                enableSorting: false,
                cell: ({ getValue }) => getDisplayValue(getValue()),
            },
            {
                id: 'arrivalDay',
                accessorKey: 'arrivalDay',
                header: 'Arrival Day',
                enableSorting: false,
                cell: ({ getValue }) => getDisplayValue(getValue()),
            },
            {
                id: 'orderSource',
                accessorKey: 'orderSource',
                header: 'Source',
                enableSorting: false,
                cell: ({ getValue }) => getDisplayValue(getValue()),
            },
            {
                id: 'shipmentStatus',
                accessorKey: 'shipmentStatus',
                header: 'Shipment Status',
                enableSorting: false,
                cell: ({ getValue }) => getDisplayValue(getValue()),
            },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: renderActionsCell,
            },
        ],
        [],
    );

    const tableMeta = useMemo(
        () => ({
            canReverseFBMSubstitution,
            downloadingBatchId,
            onApplySubstitutions,
            onDownloadFile,
            onReverseSubstitution,
        }),
        [canReverseFBMSubstitution, downloadingBatchId, onApplySubstitutions, onDownloadFile, onReverseSubstitution],
    );

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        meta: tableMeta,
    });

    const loadedCount = tableData.length;

    return (
        <div className="flex flex-1 flex-col gap-2">
            {!isLoading && loadedCount > 0 && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
                    <div className="shrink-0">
                        <ResultsCounter loadedCount={loadedCount} totalCount={loadedCount} isRefreshing={isFetching} />
                    </div>
                </div>
            )}

            <div className="flex min-h-[320px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading batches…</span>
                        </div>
                    </div>
                ) : (
                    <div className="pb-6">
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id} className="whitespace-normal break-words">
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table
                                        .getRowModel()
                                        .rows.map((row) => (
                                            <MemoizedBatchRow
                                                key={row.id}
                                                row={row}
                                                isDownloadingFile={isDownloadingBatch(downloadingBatchId, row)}
                                            />
                                        ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}

MemoizedBatchRow.displayName = 'MemoizedBatchRow';
BatchesTable.displayName = 'BatchesTable';
